// Vortx Brand Colors - Source of Truth
export const vortxColors = {
  gold: '#b28c50',           // Primary Action / Battery / Revenue
  goldHover: '#c99f6a',      // Gold hover/active state
  green: '#1f2e2b',          // App Background / Solar / OpEx
  darkGreen: '#243833',      // Darker variant for better contrast
  charcoal: '#2c2c2c',       // Component Background / Hardware
  stone: '#a6a19b',          // Secondary Text / Labels / EBITDA
  white: '#f4f1ec',          // Main Text / Loads / PDF Background
  teal: '#3f6a64',           // Borders / Grid / CapEx
  warmStone: '#a6a19b',      // Lighter card backgrounds
  gray: '#c4bfb8',           // Subtle elements
  error: '#e74c3c',          // Error / Delete / Critical
  success: '#2e7d32',        // Success / Optimal
  warning: '#f59e0b',        // Warning / Caution
};

// Retro mode accent colors — brighter variants for dark backgrounds
export const retroColors = {
  accent: '#5fd4c4',         // Bright teal accent (nav, status, highlights)
  bg: '#1a1a1a',             // Body background
  surface: '#242424',        // Card / Paper / Panel background
  input: '#1e1e1e',          // Input field background
  muted: '#888',             // Muted text
  dimmed: '#999',            // Dimmer text (timestamps, labels)
};

// Vortx Typography - Font Families
export const vortxTypography = {
  heading: "'Syne', sans-serif",
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  mono: "'JetBrains Mono', 'Courier New', monospace",
};

// Chart & Data Visualization Colors
// Use these for ALL graphs, charts, and data visualizations.
export const chartColors = {
  // Energy sources — what generates or supplies power
  solar: '#ffaa00',          // Orange — PV generation
  grid: '#1A3A5C',           // Deep navy — Grid import
  battery: '#aa88ff',        // Purple — Battery / BESS
  generator: '#2E7D32',      // Forest green — Backup generation
  ev: '#00d4aa',             // Teal — EV charging
  hvac: '#7B68EE',           // Medium purple — HVAC loads
  baseload: '#5C5C5C',       // Dark gray — Base building load

  // Energy flow outcomes
  load: '#f4f1ec',           // White — Total facility load line
  export: '#88ff44',         // Lime — Grid export
  loss: '#ff4466',           // Red — System losses
  unmetLoad: '#EF4444',      // Bright red — Unmet / critical

  // Financial
  cost: '#2196F3',           // Blue — Energy / total cost
  demand: '#f57c00',         // Deep orange — Demand charges
  savings: '#66bb6a',        // Green — Positive savings / revenue

  // Status chips & indicators
  optimal: '#66bb6a',        // Green
  infeasible: '#ff6b6b',     // Light red
  info: '#42a5f5',           // Light blue

  // Topology / system type identification (Flux PhD)
  topology: [
    '#3498db',               // Blue — AC-coupled
    '#2ecc71',               // Green — DC-coupled
    '#f39c12',               // Orange — Retrofit
    '#e74c3c',               // Red — Microgrid
    '#9b59b6',               // Purple — Charger-integrated
    '#5fd4c4',               // Teal — Fleet/specialized
  ],

  // Multi-series palette — use in order for stacked/grouped charts
  series: [
    '#b28c50',               // Gold (brand primary)
    '#3f6a64',               // Teal (brand)
    '#ffaa00',               // Solar orange
    '#aa88ff',               // Battery purple
    '#00d4aa',               // EV teal
    '#7B68EE',               // HVAC purple
    '#f57c00',               // Demand orange
    '#66bb6a',               // Savings green
  ],

  // Dark-mode series (brighter for dark backgrounds)
  seriesDark: [
    '#d4a85a',               // Bright gold
    '#5fd4c4',               // Bright teal
    '#ffaa00',               // Solar orange
    '#aa88ff',               // Battery purple
    '#00d4aa',               // EV teal
    '#7B68EE',               // HVAC purple
    '#f57c00',               // Demand orange
    '#88ff44',               // Export lime
  ],
};

// Shared component overrides for both Light and Retro mode
export const getComponentOverrides = (mode) => {
  const isRetro = mode === 'retro';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: isRetro ? retroColors.bg : vortxColors.white,
          color: isRetro ? vortxColors.white : vortxColors.charcoal,
          fontFamily: vortxTypography.body,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          padding: '10px 20px',
          fontWeight: 600,
          transition: 'all 0.3s ease',
        },
        contained: {
          boxShadow: '0 4px 12px rgba(178, 140, 80, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(178, 140, 80, 0.5)',
            transform: 'translateY(-2px)',
          },
        },
        containedPrimary: {
          backgroundColor: vortxColors.gold,
          color: isRetro ? '#FFFFFF' : vortxColors.green,
          '&:hover': {
            backgroundColor: vortxColors.goldHover,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: isRetro ? retroColors.surface : '#FFFFFF',
          border: isRetro ? `1px solid ${vortxColors.darkGreen}` : `1px solid rgba(63, 106, 100, 0.2)`,
          borderRadius: '8px',
          boxShadow: isRetro ? '0 4px 20px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: vortxColors.gold,
            boxShadow: '0 4px 12px rgba(178, 140, 80, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: isRetro ? retroColors.surface : '#FFFFFF',
          color: isRetro ? vortxColors.white : vortxColors.charcoal,
          backgroundImage: 'none', // Remove default Material UI overlay in retro mode
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: isRetro ? 'rgba(0, 0, 0, 0.3)' : vortxColors.warmStone,
          '& .MuiTableCell-head': {
            color: isRetro ? vortxColors.white : '#2a2a2a',
            fontWeight: 700,
            fontFamily: vortxTypography.body,
            borderBottom: `2px solid ${vortxColors.gold}`,
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:hover': {
            backgroundColor: 'rgba(178, 140, 80, 0.15)',
          },
          '& .MuiTableRow-root:nth-of-type(even)': {
            backgroundColor: isRetro ? 'rgba(255, 255, 255, 0.03)' : 'rgba(166, 161, 155, 0.2)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: isRetro ? `1px solid ${vortxColors.darkGreen}` : `1px solid rgba(63, 106, 100, 0.2)`,
          color: isRetro ? vortxColors.white : vortxColors.charcoal,
          fontFamily: vortxTypography.body,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: isRetro ? retroColors.input : '#FFFFFF',
            color: isRetro ? vortxColors.white : vortxColors.charcoal,
            '& fieldset': {
              borderColor: isRetro ? 'rgba(255, 255, 255, 0.2)' : 'rgba(63, 106, 100, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: vortxColors.gold,
            },
            '&.Mui-focused fieldset': {
              borderColor: vortxColors.gold,
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: isRetro ? vortxColors.stone : vortxColors.charcoal,
            fontFamily: vortxTypography.body,
            fontSize: '0.875rem',
            fontWeight: 500,
            '&.Mui-focused': {
              color: vortxColors.gold,
            },
          },
          '& .MuiInputBase-input': {
            color: isRetro ? vortxColors.white : vortxColors.charcoal,
          }
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: isRetro ? retroColors.surface : vortxColors.gold,
          color: vortxColors.white,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          borderBottom: isRetro ? `1px solid ${vortxColors.gold}` : 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: vortxTypography.body,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardWarning: {
          backgroundColor: 'rgba(237, 108, 2, 0.15)',
          color: isRetro ? '#ffcc80' : '#2a2a2a',
          '& .MuiAlert-icon': {
            color: '#ed6c02',
          },
        },
        standardSuccess: {
          backgroundColor: 'rgba(46, 125, 50, 0.15)',
          color: isRetro ? '#a5d6a7' : '#2a2a2a',
          '& .MuiAlert-icon': {
            color: isRetro ? '#66bb6a' : '#2e7d32',
          },
        },
        standardInfo: {
          backgroundColor: 'rgba(63, 106, 100, 0.15)',
          color: isRetro ? '#b2dfdb' : '#2a2a2a',
          '& .MuiAlert-icon': {
            color: isRetro ? '#5fd4c4' : vortxColors.teal,
          },
        },
        standardError: {
          backgroundColor: 'rgba(211, 47, 47, 0.15)',
          color: isRetro ? '#ef9a9a' : '#2a2a2a',
          '& .MuiAlert-icon': {
            color: isRetro ? '#ef5350' : '#d32f2f',
          },
        },
      },
    },
  };
};
